import { RefObject, useEffect, useRef, useState } from "react";

interface HoverBoundary<T extends HTMLElement> {
  isHovered: boolean;
  ref: RefObject<T>;
}

/**
 * Tracks whether the cursor is within an element's bounding box using a window-level
 * mousemove listener rather than onMouseEnter/onMouseLeave. Recharts' Tooltip "active"
 * state can get stuck on if a discrete mouseleave is ever missed (e.g. fast cursor
 * movement between adjacent charts); this re-evaluates on every mousemove anywhere on
 * the page, so it self-corrects on the very next tick instead of depending on one event
 * firing correctly.
 */
export function useHoverBoundary<T extends HTMLElement = HTMLDivElement>(): HoverBoundary<T> {
  const ref = useRef<T>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handlePointerMove = (event: MouseEvent): void => {
      const rect = ref.current?.getBoundingClientRect();
      const inside = !!rect
        && event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom;
      setIsHovered(inside);
    };

    window.addEventListener("mousemove", handlePointerMove);
    return () => window.removeEventListener("mousemove", handlePointerMove);
  }, []);

  return { isHovered, ref };
}
